// ─────────────────────────────────────────────────────────────────
// Client-side Keycloak wrapper.
//
// PATH B — foundation scaffolding. This file is intentionally
// minimal; wire it up when the Keycloak realm is provisioned.
//
// Design:
//   * We use `keycloak-js` (official client) for the OIDC dance.
//   * Access token lives in memory. Refresh token in memory too
//     (auto-refresh via keycloak-js). NOT in localStorage — reduces
//     XSS blast radius.
//   * The issuer + client id are fetched from the server at runtime
//     via GET /api/auth/config (backed by the server-side
//     KEYCLOAK_ISSUER / KEYCLOAK_CLIENT_ID env vars), NOT baked into
//     the bundle at build time. This lets a single build be deployed
//     against different realms without recompiling.
//       issuer:   https://auth.40geo.com/realms/nuggets
//       clientId: the browser SPA client registered in Keycloak
//
// USAGE:
//   import { getKeycloak, getAccessToken, login, logout } from './auth';
//   await getKeycloak().init({ onLoad: 'check-sso' });
//   const token = getAccessToken();
//   fetch('/api/opportunities', { headers: { Authorization: `Bearer ${token}` } });
//
// Needs the `keycloak-js` npm dep — see package.json update.
// ─────────────────────────────────────────────────────────────────

let _kc = null;
let _configPromise = null;

/**
 * Fetch the runtime Keycloak config from the server once, then cache
 * the promise so concurrent callers share a single request. Uses a
 * plain fetch (no auth) — this must resolve before the user is logged
 * in, and importing the authenticated `api` client here would create a
 * circular dependency (api.js imports authFetch from this file).
 */
export async function getAuthConfig() {
  if (_configPromise) return _configPromise;
  _configPromise = (async () => {
    const res = await fetch('/api/auth/config');
    if (!res.ok) {
      _configPromise = null; // allow a retry on the next call
      throw new Error(`failed to load auth config: HTTP ${res.status}`);
    }
    const body = await res.json();
    if (!body?.issuer || !body?.clientId) {
      _configPromise = null;
      throw new Error('auth config missing issuer/clientId');
    }
    return { issuer: body.issuer, clientId: body.clientId };
  })();
  return _configPromise;
}

/**
 * Lazy-load keycloak-js (dynamic import so it doesn't bloat initial bundle
 * for logged-out users hitting the landing page).
 */
export async function getKeycloak() {
  if (_kc) return _kc;
  const KeycloakDefault = (await import('keycloak-js')).default;
  const { issuer, clientId } = await getAuthConfig();
  // keycloak-js expects url + realm as separate fields, not the full
  // issuer URL. Parse it.
  const m = /^(.+)\/realms\/([^/]+)$/.exec(issuer);
  if (!m) throw new Error(`KEYCLOAK_ISSUER must look like https://.../realms/<realm>, got: ${issuer}`);
  _kc = new KeycloakDefault({
    url:      m[1],
    realm:    m[2],
    clientId,
  });
  return _kc;
}

/**
 * Initialize + attempt SSO. Returns true if the user has an active
 * session (either via a valid refresh token or a Keycloak SSO
 * cookie from another app on the same realm). Call this once at
 * app startup.
 */
export async function initAuth() {
  const kc = await getKeycloak();

  // keycloak-js `check-sso` runs its session probe in a hidden iframe.
  // When Keycloak is misconfigured for this origin (a clientId that doesn't
  // exist in the realm, an unregistered redirect_uri) — or the browser
  // blocks third-party cookies — that iframe silently never posts back and
  // kc.init() hangs *forever*, leaving the whole app stuck on the "Loading…"
  // shell with no recourse. Race it against a timeout so a hang becomes a
  // recoverable, visible outcome instead of a dead spinner. The caller
  // (main.jsx) falls back to a top-level login redirect, which isn't subject
  // to the iframe/cookie limits and surfaces a real config error if there
  // is one.
  const SSO_TIMEOUT_MS = 8000;
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const err = new Error('Keycloak SSO check timed out');
      err.code = 'SSO_TIMEOUT';
      reject(err);
    }, SSO_TIMEOUT_MS);
  });

  try {
    const authenticated = await Promise.race([
      kc.init({
        onLoad:            'check-sso',           // don't redirect, just check
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        checkLoginIframe:  false,                 // avoids issues with sandboxed iframes
        pkceMethod:        'S256',                // more secure than implicit
      }),
      timeout,
    ]);
    // Auto-refresh access token when < 30s from expiry
    kc.onTokenExpired = () => kc.updateToken(30).catch(() => login());
    return authenticated;
  } finally {
    clearTimeout(timer);
  }
}

export function getAccessToken() {
  return _kc?.token || null;
}

export function getUserClaims() {
  return _kc?.tokenParsed || null;
}

export function login(redirectUri) {
  return _kc?.login({ redirectUri: redirectUri || window.location.href });
}

export function logout(redirectUri) {
  return _kc?.logout({ redirectUri: redirectUri || window.location.origin });
}

export function isAuthenticated() {
  return !!_kc?.authenticated;
}

/**
 * Convenience wrapper for authenticated fetch. Prepends Bearer
 * token + refreshes if within 30s of expiry.
 */
export async function authFetch(url, opts = {}) {
  const kc = await getKeycloak();
  if (kc.token) {
    // Refresh the token if it's about to expire — avoids the request
    // failing mid-flight because of a stale JWT.
    try { await kc.updateToken(30); } catch { /* fallthrough with old token */ }
  }
  const headers = { ...(opts.headers || {}) };
  if (kc.token) headers['Authorization'] = `Bearer ${kc.token}`;
  return fetch(url, { ...opts, headers });
}
